import { isPlatformBrowser, NgStyle } from '@angular/common';
import { DestroyRef, DOCUMENT, effect, ElementRef, EventEmitter, inject, input, PLATFORM_ID } from '@angular/core';
import { UiDragDropModule } from '@common/ui/frontend-shared/dragdrop';
import { UiVideoPlayerComponent } from '@common/ui/frontend-shared/video-player';
import { takeWhileAlive } from '@core/frontend-shared';
import { normalizeMouseWheel } from '@core/frontend-shared/utils/dom';
import { fromEvent, throttleTime } from 'rxjs';
import { MediaAssetTimelineTrackComponent } from './media-asset-timeline-track.component';
import { TimelineService } from './timeline.service';
import * as i0 from "@angular/core";
import * as i1 from "../../../../../../ui/frontend-shared/src/lib/dragdrop/draggable.directive";
const _c0 = ["canvas"];
const _c1 = ["timelineScroller"];
const _c2 = ["timelineWrapper"];
const _c3 = a0 => ({
  height: a0
});
const _c4 = a0 => ({
  left: a0
});
const _c5 = (a0, a1) => ({
  left: a0,
  right: a1
});
function MediaAssetTimelineComponent_Conditional_11_For_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "media-asset-timeline-track", 13);
    i0.ɵɵlistener("addItemAt", function MediaAssetTimelineComponent_Conditional_11_For_2_Template_media_asset_timeline_track_addItemAt_0_listener($event) {
      const track_r2 = i0.ɵɵrestoreView(_r1).$implicit;
      const ctx_r2 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r2.handleAddItemAt($event, track_r2.id));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const track_r2 = ctx.$implicit;
    const details_r4 = i0.ɵɵnextContext();
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵproperty("trackId", track_r2.id)("length", details_r4.duration)("items", track_r2.items)("selection", ctx_r2.timeline.selection());
  }
}
function MediaAssetTimelineComponent_Conditional_11_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 11);
    i0.ɵɵrepeaterCreate(1, MediaAssetTimelineComponent_Conditional_11_For_2_Template, 1, 4, "media-asset-timeline-track", 12, i0.ɵɵrepeaterTrackByIdentity);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngStyle", i0.ɵɵpureFunction2(1, _c5, ctx_r2.timeline.timelinePadding() + "px", ctx_r2.timeline.timelinePadding() + "px"));
    i0.ɵɵadvance();
    i0.ɵɵrepeater(ctx_r2.timeline.tracksWithItems());
  }
}
const ZOOM_SPEED = .1;
export let MediaAssetTimelineComponent = /*#__PURE__*/(() => {
  class MediaAssetTimelineComponent {
    constructor() {
      this.subtitles = input([], ...(ngDevMode ? [{
        debugName: "subtitles"
      }] : /* istanbul ignore next */[]));
      this.enablePlayer = input(true, ...(ngDevMode ? [{
        debugName: "enablePlayer"
      }] : /* istanbul ignore next */[]));
      // autofocusPlayer = input<boolean>(true);
      this.playPauseWithSpace = input(true, ...(ngDevMode ? [{
        debugName: "playPauseWithSpace"
      }] : /* istanbul ignore next */[]));
      this.pxPerSec = input(50, ...(ngDevMode ? [{
        debugName: "pxPerSec"
      }] : /* istanbul ignore next */[]));
      this.currPositionSec = input(0, ...(ngDevMode ? [{
        debugName: "currPositionSec"
      }] : /* istanbul ignore next */[]));
      this.destroyRef = inject(DestroyRef);
      this.doc = inject(DOCUMENT);
      this.isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
      this._selectionChange = effect(() => {
        const selection = this.timeline.selection();
        const selectedItem = this.timeline.items().find(item => item.id === selection);
        this.onSelect.emit(selectedItem);
      }, ...(ngDevMode ? [{
        debugName: "_selectionChange"
      }] : /* istanbul ignore next */[]));
      this.onSelect = new EventEmitter();
      this.onTimelineService = new EventEmitter();
      this.onAddItemAt = new EventEmitter();
    }
    handleKeypress(event) {
      if (!this.playPauseWithSpace) return;
      if (event.code === 'Space' && this.player) {
        this.player.togglePlay();
      }
    }
    updateAvailWidth() {
      const width = this.scrollRef.nativeElement.offsetWidth - 2;
      this.timeline.updateAvailWidth(width);
    }
    ngOnInit() {
      this.timeline.initialize(this.scrollRef, this.canvasRef);
      this.updateAvailWidth();
      this.onTimelineService.emit(this.timeline);
    }
    ngAfterViewInit() {
      if (this.isBrowser) {
        this.player.onPlayerProgress$.pipe(takeWhileAlive(this.destroyRef)).subscribe(progress => {
          this.timeline.currPositionSec.set(progress);
        });
        this.player.onPlayerProgress$.pipe(
        // TODO: throttling may need to become dynamic, if we zoom in, the refresh has to happen more frequently
        // also note that for short intervals, timeline scroll animations may interfere with each other!
        throttleTime(500)).subscribe(progress => {
          this.timeline.updateTimelineScrollOffset();
        });
        fromEvent(window, 'resize').pipe(takeWhileAlive(this.destroyRef), throttleTime(100)).subscribe(e => {
          this.updateAvailWidth();
        });
        // if(this.autofocusPlayer) this.player.focus();
      }
    }
    // ======  Timeline Interaction  ============================================================================================
    onClickCanvas(e) {
      const newTime = this.timeline.pxPositionToTimelineSeconds(e.offsetX).timelineSeconds;
      this.timeline.currPositionSec.set(newTime); // required in case the player is paused as it won't emit progress event then
      this.player.setCurrentTime(newTime, true);
      // this.currPositionSec.set()
    }
    onMouseWheel(e) {
      let hoveredTimelineXPos;
      if (e.target === this.wrapperRef.nativeElement) {
        hoveredTimelineXPos = e.offsetX;
      } else {
        const rect = this.wrapperRef.nativeElement.getBoundingClientRect();
        hoveredTimelineXPos = e.pageX - rect.x;
      }
      const wheel = normalizeMouseWheel(e);
      const zoomIn = wheel.spinY < 0;
      this.timeline.zoomAt(hoveredTimelineXPos, zoomIn);
    }
    onDragTimeline(e) {
      this.timeline.scrollTo(-1 * e.deltaX, true, false);
      if (Math.abs(e.deltaY) > 2) {
        const zoomIn = e.deltaY > 0;
        this.timeline.zoomAt(e.offsetX, zoomIn);
      }
    }
    handleAddItemAt(e, trackId) {
      this.onAddItemAt.emit({
        seconds: e.seconds,
        trackId
      });
    }
    static {
      this.ɵfac = function MediaAssetTimelineComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MediaAssetTimelineComponent)();
      };
    }
    static {
      this.ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
        type: MediaAssetTimelineComponent,
        selectors: [["media-asset-timeline"]],
        viewQuery: function MediaAssetTimelineComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c0, 7)(_c1, 7)(_c2, 7)(UiVideoPlayerComponent, 7);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.canvasRef = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.scrollRef = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.wrapperRef = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.player = _t.first);
          }
        },
        hostBindings: function MediaAssetTimelineComponent_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("keypress", function MediaAssetTimelineComponent_keypress_HostBindingHandler($event) {
              return ctx.handleKeypress($event);
            }, i0.ɵɵresolveWindow);
          }
        },
        inputs: {
          subtitles: [1, "subtitles"],
          enablePlayer: [1, "enablePlayer"],
          playPauseWithSpace: [1, "playPauseWithSpace"],
          pxPerSec: [1, "pxPerSec"],
          currPositionSec: [1, "currPositionSec"],
          timeline: "timeline"
        },
        outputs: {
          onSelect: "onSelect",
          onTimelineService: "onTimelineService",
          onAddItemAt: "onAddItemAt"
        },
        decls: 12,
        vars: 13,
        consts: [["player", ""], ["timelineScroller", ""], ["timelineWrapper", ""], ["canvas", ""], [1, "preview-wrapper"], [3, "url", "stretch", "hotkeys", "enableScrubber", "autoplay", "subtitles"], [1, "timeline-scroll", 3, "ngStyle"], [1, "timeline-wrapper", 3, "mousewheel"], [1, "timeline", 3, "click"], [1, "playhead", 3, "ngStyle"], ["uiDraggable", "", "ghostMode", "hide", 1, "dragarea", 3, "onDrag"], [1, "tracks", 3, "ngStyle"], [3, "trackId", "length", "items", "selection"], [3, "addItemAt", "trackId", "length", "items", "selection"]],
        template: function MediaAssetTimelineComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 4);
            i0.ɵɵelement(1, "ui-video-player", 5, 0);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(3, "div", 6, 1)(5, "div", 7, 2);
            i0.ɵɵlistener("mousewheel", function MediaAssetTimelineComponent_Template_div_mousewheel_5_listener($event) {
              return ctx.onMouseWheel($event);
            });
            i0.ɵɵelementStart(7, "canvas", 8, 3);
            i0.ɵɵlistener("click", function MediaAssetTimelineComponent_Template_canvas_click_7_listener($event) {
              return ctx.onClickCanvas($event);
            });
            i0.ɵɵelementEnd();
            i0.ɵɵelement(9, "div", 9);
            i0.ɵɵelementStart(10, "div", 10);
            i0.ɵɵlistener("onDrag", function MediaAssetTimelineComponent_Template_div_onDrag_10_listener($event) {
              return ctx.onDragTimeline($event);
            });
            i0.ɵɵelementEnd();
            i0.ɵɵconditionalCreate(11, MediaAssetTimelineComponent_Conditional_11_Template, 3, 4, "div", 11);
            i0.ɵɵelementEnd()();
          }
          if (rf & 2) {
            let tmp_4_0;
            let tmp_12_0;
            i0.ɵɵadvance();
            i0.ɵɵproperty("url", (tmp_4_0 = ctx.timeline.assetInfo()) == null ? null : tmp_4_0.preview == null ? null : tmp_4_0.preview.url)("stretch", true)("hotkeys", false)("enableScrubber", false)("autoplay", false)("subtitles", ctx.subtitles());
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngStyle", i0.ɵɵpureFunction1(9, _c3, ctx.timeline.timelineHeight() + "px"));
            i0.ɵɵadvance(6);
            i0.ɵɵproperty("ngStyle", i0.ɵɵpureFunction1(11, _c4, ctx.timeline.playheadX() + "px"));
            i0.ɵɵadvance(2);
            i0.ɵɵconditional((tmp_12_0 = (tmp_12_0 = ctx.timeline.assetInfo()) == null ? null : tmp_12_0.details) ? 11 : -1, tmp_12_0);
          }
        },
        dependencies: [MediaAssetTimelineTrackComponent, NgStyle, UiVideoPlayerComponent, UiDragDropModule, i1.Draggable],
        styles: ["[_nghost-%COMP%]{display:block;background:var(--backgroundSoftColor)}[_nghost-%COMP%]{height:800px;display:flex;flex-direction:column}.preview-wrapper[_ngcontent-%COMP%]{padding:0 0 20px;flex:1}ui-video-player[_ngcontent-%COMP%]{height:100%}.timeline-scroll[_ngcontent-%COMP%]{overflow-x:scroll}.timeline-scroll[_ngcontent-%COMP%]{scrollbar-width:none;-ms-overflow-style:none}.timeline-scroll[_ngcontent-%COMP%]::-webkit-scrollbar{display:none}.timeline-scroll[_ngcontent-%COMP%]{min-height:-moz-fit-content;min-height:fit-content}.timeline-wrapper[_ngcontent-%COMP%]{position:relative;width:-moz-fit-content;width:fit-content}.timeline[_ngcontent-%COMP%]{cursor:text}.playhead[_ngcontent-%COMP%]{position:absolute;top:0;bottom:18%;width:2px;background:var(--contrastColor);pointer-events:none;z-index:100}.dragarea[_ngcontent-%COMP%]{position:absolute;left:0;right:0;bottom:0;height:30%;cursor:move;opacity:0;background:var(--contrastColor);-webkit-mask-image:linear-gradient(180deg,rgba(0,0,0,0),black);mask-image:linear-gradient(180deg,#0000,#000)}.dragarea[_ngcontent-%COMP%]:hover{opacity:.2}.tracks[_ngcontent-%COMP%]{position:absolute;top:10px;left:0;right:0}"],
        changeDetection: 0
      });
    }
  }
  return MediaAssetTimelineComponent;
})();